import React, { useEffect, useState } from "react";
import styled from "styled-components";
import supabase from "../supabase"; // adjust import path as needed

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${({ open }) => (open ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;
const Sheet = styled.div`
  width: 680px;
  max-height: 85vh;
  overflow: auto;
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
`;
const Row = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 12px;
  align-items: start;
  margin-bottom: 14px;
`;
const Label = styled.label`
  font-weight: 600;
`;
const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
`;
const TextArea = styled.textarea`
  width: 100%;
  min-height: 70px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
  resize: vertical;
`;
const Inline = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;
const Select = styled.select`
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
  width: 100%;
`;
const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #eef2ff;
  color: #1f2937;
  border: 1px solid #dbeafe;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 12px;
  margin: 4px 6px 0 0;
`;
const X = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  font-weight: 700;
`;
const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;
const Btn = styled.button`
  border: 1px solid #ddd;
  background: #f9f9f9;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  &.primary {
    background: #2563eb;
    color: #fff;
    border-color: #2563eb;
  }
`;

export default function CreateProjectModal({
  isOpen,
  onClose,
  onCreate,
  orgId,
  currentUserRole,
  lockOrg = false,
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [writers, setWriters] = useState([]);
  const [editors, setEditors] = useState([]);
  const [selectedWriterIds, setSelectedWriterIds] = useState([]);
  const [selectedEditorIds, setSelectedEditorIds] = useState([]);
  const [writerText, setWriterText] = useState("");
  const [editorText, setEditorText] = useState("");
  const [loading, setLoading] = useState(false);

  // NEW: Organization State
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(orgId || "");

  // Admin gate (hide if not admin)
  const isAdmin = (currentUserRole || "").toLowerCase() === "admin";

  useEffect(() => {
    if (!isOpen) return;

    // Set the default dropdown value to the current portal's orgId whenever the modal opens
    if (orgId) setSelectedOrgId(orgId);

    (async () => {
      setLoading(true);
      try {
        // Fetch Organizations for the dropdown
        const { data: orgData, error: orgError } = await supabase
          .from("Organizations")
          .select("id, name")
          .order("name", { ascending: true });

        if (orgError) throw orgError;
        setOrganizations(orgData || []);

        // Fetch org members and split by role
        if (orgId) {
          const { data: userData, error: userError } = await supabase
            .from("Users")
            .select("id, name, email, role")
            .eq("company_id", orgId);
          if (userError) throw userError;

          const ws = (userData || []).filter((u) =>
            ["writer"].includes((u.role || "").toLowerCase())
          );
          const es = (userData || []).filter((u) =>
            ["editor"].includes((u.role || "").toLowerCase())
          );

          setWriters(ws);
          setEditors(es);
        }
      } catch (e) {
        console.error(e);
        alert("Failed to load modal data.");
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, orgId]);

  // Helpers to maintain textarea text from id selections
  const namesFromIds = (ids, pool) =>
    ids
      .map((id) => pool.find((p) => p.id === id))
      .filter(Boolean)
      .map((u) => u.name || u.email)
      .join(", ");

  const addWriter = (id) => {
    if (!id) return;
    if (selectedWriterIds.includes(id)) return;
    const next = [...selectedWriterIds, id];
    setSelectedWriterIds(next);
    setWriterText(namesFromIds(next, writers));
  };
  const addEditor = (id) => {
    if (!id) return;
    if (selectedEditorIds.includes(id)) return;
    const next = [...selectedEditorIds, id];
    setSelectedEditorIds(next);
    setEditorText(namesFromIds(next, editors));
  };

  const removeWriter = (id) => {
    const next = selectedWriterIds.filter((x) => x !== id);
    setSelectedWriterIds(next);
    setWriterText(namesFromIds(next, writers));
  };
  const removeEditor = (id) => {
    const next = selectedEditorIds.filter((x) => x !== id);
    setSelectedEditorIds(next);
    setEditorText(namesFromIds(next, editors));
  };

  const handleCreate = async () => {
    if (!title.trim()) return alert("Please enter a project title.");
    try {
      setLoading(true);

      // Find the name of the selected organization to pass along
      const selectedOrg = organizations.find((o) => o.id === selectedOrgId);

      await onCreate?.({
        title: title.trim(),
        org_id: selectedOrgId || null,
        org_name: selectedOrg ? selectedOrg.name : "Not Assigned",
        description: description.trim(),
        writer_ids: selectedWriterIds,
        editor_ids: selectedEditorIds,
      });

      // reset
      setTitle("");
      setSelectedOrgId(orgId || "");
      setDescription("");
      setSelectedWriterIds([]);
      setSelectedEditorIds([]);
      setWriterText("");
      setEditorText("");
      onClose?.();
    } catch (e) {
      console.error(e);
      alert("Failed to create project.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Backdrop open={isOpen}>
      <Sheet>
        <h3 style={{ marginTop: 0 }}>Create a Project</h3>

        <Row>
          <Label>Project Title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </Row>

        {/* ✅ UPDATED: Organization Dropdown */}
        <Row>
          <Label>Organization</Label>
          {lockOrg ? (
            <Input
              value={
                organizations.find((o) => o.id === selectedOrgId)?.name || ""
              }
              disabled
            />
          ) : (
            <Select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              disabled={loading}
            >
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </Select>
          )}
        </Row>

        <Row>
          <Label>Project Description</Label>
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Row>

        <Row>
          <Label>Assign Writers</Label>
          <div>
            <Inline>
              <Select
                onChange={(e) => {
                  addWriter(e.target.value);
                  e.target.value = "";
                }}
                defaultValue=""
                disabled={loading}
              >
                <option value="" disabled>
                  {loading ? "Loading..." : "Select a writer"}
                </option>
                {writers.map((w) => (
                  <option key={w.id} value={w.id}>
                    {(w.name || w.email) + " · " + (w.role || "Writer")}
                  </option>
                ))}
              </Select>
            </Inline>

            <div style={{ marginTop: 8 }}>
              {selectedWriterIds.map((id) => {
                const u = writers.find((x) => x.id === id);
                if (!u) return null;
                return (
                  <Pill key={id}>
                    {u.name || u.email}
                    <X onClick={() => removeWriter(id)} aria-label="Remove">
                      ×
                    </X>
                  </Pill>
                );
              })}
            </div>

            <TextArea
              style={{ marginTop: 8 }}
              value={writerText}
              onChange={(e) => setWriterText(e.target.value)}
              readOnly
            />
          </div>
        </Row>

        <Row>
          <Label>Assign Editors</Label>
          <div>
            <Inline>
              <Select
                onChange={(e) => {
                  addEditor(e.target.value);
                  e.target.value = "";
                }}
                defaultValue=""
                disabled={loading}
              >
                <option value="" disabled>
                  {loading ? "Loading..." : "Select an editor"}
                </option>
                {editors.map((ed) => (
                  <option key={ed.id} value={ed.id}>
                    {(ed.name || ed.email) + " · " + (ed.role || "Editor")}
                  </option>
                ))}
              </Select>
            </Inline>

            <div style={{ marginTop: 8 }}>
              {selectedEditorIds.map((id) => {
                const u = editors.find((x) => x.id === id);
                if (!u) return null;
                return (
                  <Pill key={id}>
                    {u.name || u.email}
                    <X onClick={() => removeEditor(id)} aria-label="Remove">
                      ×
                    </X>
                  </Pill>
                );
              })}
            </div>

            <TextArea
              style={{ marginTop: 8 }}
              value={editorText}
              onChange={(e) => setEditorText(e.target.value)}
              readOnly
            />
          </div>
        </Row>

        <Footer>
          <Btn onClick={onClose} disabled={loading}>
            Cancel
          </Btn>
          <Btn
            className="primary"
            onClick={handleCreate}
            disabled={!isAdmin || loading}
          >
            {loading ? "Creating…" : "Create Project"}
          </Btn>
        </Footer>
      </Sheet>
    </Backdrop>
  );
}
